import { Injectable } from '@angular/core';
import {
    ConnectionBackend,
    Headers,
    Http,
    Request,
    RequestOptions,
    RequestOptionsArgs,
    Response
} from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import * as CryptoJS from '../../crypto-js';
import * as AntiForgeryTokenConfig from '../AntiForgeryTokenConfig';
import { Utils } from '../utils';
import { passwordEncryption } from '../globals';
import { TokenRefreshService } from './token-refresh.service';
import { genericConfigValues } from '../globals';

@Injectable()
export class RefreshingHttp extends Http {
    constructor(
        backend: ConnectionBackend,
        defaultOptions: RequestOptions,
        private tokenRefreshService: TokenRefreshService
    ) {
        super(backend, defaultOptions);
    }

    request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
        return super.request(url, options || {}).catch(error => {
            if (error.status !== 401 || this.isTokenRequest(url) || !this.hasAuthorization(url, options)
                || !this.tokenRefreshService.hasRefreshToken()) {
                return Observable.throw(error);
            }

            return Observable.fromPromise(this.tokenRefreshService.refreshAccessToken())
                .switchMap(accessToken => super.request(this.withUpdatedAuthorization(url, options || {}, accessToken)));
        });
    }

    private isTokenRequest(url: string | Request): boolean {
        const requestUrl = url instanceof Request ? url.url : url;
        return /\/token(?:[?#]|$)/i.test(requestUrl);
    }

    private hasAuthorization(url: string | Request, options?: RequestOptionsArgs): boolean {
        const headers = url instanceof Request ? url.headers : options && options.headers;
        return !!headers && !!headers.get('Authorization');
    }

    private withUpdatedAuthorization(
        url: string | Request,
        options: RequestOptionsArgs,
        accessToken: string
    ): Request {
        if (url instanceof Request) {
            const headers = new Headers(url.headers);
            headers.set('Authorization', accessToken);
            if (headers.has('SecretKey')) {
                headers.set('SecretKey', this.generateAntiForgeryToken(accessToken));
            }
            return new Request({
                method: url.method,
                url: url.url,
                headers: headers,
                body: url.getBody(),
                responseType: url.responseType,
                withCredentials: url.withCredentials
            });
        }

        const retryHeaders = new Headers(options && options.headers);
        retryHeaders.set('Authorization', accessToken);
        if (retryHeaders.has('SecretKey')) {
            retryHeaders.set('SecretKey', this.generateAntiForgeryToken(accessToken));
        }
        const retryOptions = new RequestOptions(options).merge({ url: url, headers: retryHeaders });
        return new Request(retryOptions);
    }

    private getUserId(): string {
        return genericConfigValues.clientPortal
            ? localStorage.getItem('loggedInUserId') || ''
            : sessionStorage.getItem('loggedInUserId') || '';
    }

    private generateAntiForgeryToken(accessToken: string): string {
        const timestamp = new Date().getTime().toString();
        const guid = Utils.createGuid();
        const tokenData = accessToken + guid + timestamp;
        const encryptedHash = CryptoJS.enc.Base64.stringify(CryptoJS.HmacSHA256(
            AntiForgeryTokenConfig.AntiForgeryTokenProperies.Token + tokenData,
            tokenData
        ));
        const payload = encryptedHash + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + tokenData
            + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + timestamp
            + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + accessToken
            + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + this.getUserId();
        const key = CryptoJS.enc.Utf8.parse(passwordEncryption.passwordEncryptionKey);
        const iv = CryptoJS.enc.Utf8.parse(passwordEncryption.passwordEncryptionIV);

        return CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(payload), key, {
            keySize: 128 / 8,
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        }).toString();
    }
}